/**
 * MunchMakers Main Application Entry
 * Replaces @bigcommerce/stencil-utils
 */
// B2B MunchMakers Theme
import '../scss/theme-b2b.scss';
// Core modules
import { Cart } from './modules/cart';
import { QuickSearch } from './modules/quick-search';
import { Navigation } from './modules/navigation';
import { ProductGallery } from './modules/product-gallery';
import { ProductOptions } from './modules/product-options';
import { AIMockupModalV2 } from './modules/ai-mockup-v2';
import { QuantitySelector } from './modules/quantity-selector';
import { Newsletter } from './modules/newsletter';
import { LazyLoad } from './modules/lazy-load';
import { FormValidation } from './modules/form-validation';
import { CallbackModal } from './modules/callback-modal';
import { initTranslator } from './modules/translator';
// Initialize application
class App {
    constructor() {
        this.cart = new Cart();
        this.quickSearch = new QuickSearch();
        this.navigation = new Navigation();
        this.lazyLoad = new LazyLoad();
        this.init();
    }
    init() {
        // Wait for DOM
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.onReady());
        }
        else {
            this.onReady();
        }
    }
    onReady() {
        // Initialize page-specific modules
        this.initProductPage();
        this.initCartPage();
        this.initForms();
        this.initNewsletter();
        this.initQuantitySelectors();
        this.initWishlist();
        this.initReviews();
        this.initStarRatings();
        this.initCallbackModal();
        // Initialize lazy loading
        this.lazyLoad.init();
        // Initialize translator for non-English pages
        initTranslator();
        // Dispatch ready event
        document.dispatchEvent(new CustomEvent('mm:ready'));
    }
    initProductPage() {
        const productContainer = document.querySelector('[data-product]');
        if (!productContainer)
            return;
        // Initialize product gallery
        const galleryEl = document.querySelector('[data-product-gallery]');
        if (galleryEl) {
            new ProductGallery(galleryEl);
        }
        // Initialize product options
        const optionsEl = document.querySelector('[data-product-options]');
        if (optionsEl) {
            new ProductOptions(optionsEl);
        }
        // Initialize AI mockup modal V2 (test mode only - design tool UI)
        new AIMockupModalV2();
    }
    initCartPage() {
        const cartContainer = document.querySelector('[data-cart]');
        if (!cartContainer)
            return;
        // Cart is already initialized globally
    }
    initForms() {
        document.querySelectorAll('form[data-validate]').forEach((form) => {
            new FormValidation(form);
        });
    }
    initNewsletter() {
        document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
            new Newsletter(form);
        });
    }
    initQuantitySelectors() {
        document.querySelectorAll('.quantity-selector').forEach((el) => {
            new QuantitySelector(el);
        });
    }
    initWishlist() {
        // Initialize wishlist toggle buttons (for product cards/pages)
        document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.preventDefault();
                const button = e.currentTarget;
                const productId = button.dataset.productId;
                const variantId = button.dataset.variantId;
                const isInWishlist = button.classList.contains('in-wishlist');
                if (!productId)
                    return;
                try {
                    button.classList.add('loading');
                    const endpoint = isInWishlist ? '/account/wishlist/remove' : '/account/wishlist/add';
                    const response = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                        },
                        body: JSON.stringify({
                            productId,
                            variantId,
                        }),
                    });
                    const data = await response.json();
                    if (data.success) {
                        // Toggle the state
                        button.classList.toggle('in-wishlist');
                        // Update aria label
                        const newState = button.classList.contains('in-wishlist');
                        button.setAttribute('aria-label', newState ? 'Remove from wishlist' : 'Add to wishlist');
                        // Show notification
                        this.showNotification(newState ? 'Added to wishlist!' : 'Removed from wishlist', 'success');
                    }
                    else if (response.status === 401) {
                        // User not logged in
                        this.showNotification('Please sign in to use wishlist', 'warning');
                        // Redirect to login after a short delay
                        setTimeout(() => {
                            window.location.href = '/auth/login?redirect=' + encodeURIComponent(window.location.pathname);
                        }, 1500);
                    }
                    else {
                        this.showNotification(data.error || 'Failed to update wishlist', 'error');
                    }
                }
                catch (error) {
                    this.showNotification('Failed to update wishlist', 'error');
                }
                finally {
                    button.classList.remove('loading');
                }
            });
        });
        // Initialize remove buttons on wishlist page
        document.querySelectorAll('[data-remove-wishlist]').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.preventDefault();
                const button = e.currentTarget;
                const productId = button.dataset.productId;
                if (!productId)
                    return;
                try {
                    button.classList.add('loading');
                    const response = await fetch('/account/wishlist/remove', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                        },
                        body: JSON.stringify({ productId }),
                    });
                    const data = await response.json();
                    if (data.success) {
                        // Remove the wishlist item from the DOM
                        const wishlistItem = button.closest('[data-wishlist-item]');
                        if (wishlistItem) {
                            wishlistItem.classList.add('removing');
                            setTimeout(() => {
                                wishlistItem.remove();
                                // Check if wishlist is now empty
                                const remainingItems = document.querySelectorAll('[data-wishlist-item]');
                                if (remainingItems.length === 0) {
                                    // Reload page to show empty state
                                    window.location.reload();
                                }
                            }, 300);
                        }
                        this.showNotification('Removed from wishlist', 'success');
                    }
                    else {
                        this.showNotification(data.error || 'Failed to remove item', 'error');
                    }
                }
                catch (error) {
                    this.showNotification('Failed to remove item', 'error');
                }
                finally {
                    button.classList.remove('loading');
                }
            });
        });
    }
    initStarRatings() {
        // Initialize all star rating displays based on data-rating attribute
        document.querySelectorAll('.stars[data-rating], [data-rating] .stars').forEach((starsEl) => {
            const container = starsEl.closest('[data-rating]') || starsEl;
            const rating = parseFloat(container.dataset.rating || '0');
            const stars = starsEl.querySelectorAll('svg');
            stars.forEach((star, i) => {
                star.classList.remove('filled', 'half-filled');
                if (i < Math.floor(rating)) {
                    star.classList.add('filled');
                }
                else if (i < rating && rating % 1 >= 0.5) {
                    star.classList.add('half-filled');
                }
            });
        });
    }
    initCallbackModal() {
        // Initialize callback request modal
        new CallbackModal();
    }
    initReviews() {
        const reviewsContainer = document.querySelector('.product-reviews');
        if (!reviewsContainer)
            return;
        // Toggle review form
        const toggleBtn = reviewsContainer.querySelector('[data-review-form-toggle]');
        const formContainer = reviewsContainer.querySelector('[data-review-form-container]');
        toggleBtn?.addEventListener('click', () => {
            formContainer?.toggleAttribute('hidden');
            if (formContainer && !formContainer.hasAttribute('hidden')) {
                formContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });
        // Rating stars input
        const ratingInput = reviewsContainer.querySelector('[data-rating-input]');
        const ratingHiddenInput = ratingInput?.querySelector('input[type="hidden"]');
        const ratingStars = ratingInput?.querySelectorAll('.rating-star');
        ratingStars?.forEach((star) => {
            star.addEventListener('click', () => {
                const rating = star.dataset.rating;
                if (rating && ratingHiddenInput) {
                    ratingHiddenInput.value = rating;
                    // Update visual state
                    ratingStars.forEach((s, i) => {
                        const starRating = parseInt(s.dataset.rating || '0');
                        if (starRating <= parseInt(rating)) {
                            s.classList.add('active');
                        }
                        else {
                            s.classList.remove('active');
                        }
                    });
                }
            });
            star.addEventListener('mouseenter', () => {
                const rating = parseInt(star.dataset.rating || '0');
                ratingStars.forEach((s) => {
                    const starRating = parseInt(s.dataset.rating || '0');
                    if (starRating <= rating) {
                        s.classList.add('hover');
                    }
                    else {
                        s.classList.remove('hover');
                    }
                });
            });
        });
        ratingInput?.addEventListener('mouseleave', () => {
            ratingStars?.forEach((s) => s.classList.remove('hover'));
        });
        // Submit review form
        const reviewForm = reviewsContainer.querySelector('[data-review-form]');
        reviewForm?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const productId = reviewForm.dataset.productId;
            if (!productId)
                return;
            const formData = new FormData(reviewForm);
            const data = {
                rating: parseInt(formData.get('rating')),
                title: formData.get('title'),
                body: formData.get('body'),
                name: formData.get('name'),
                email: formData.get('email'),
            };
            if (!data.rating || data.rating < 1 || data.rating > 5) {
                this.showNotification('Please select a rating', 'error');
                return;
            }
            const submitBtn = reviewForm.querySelector('button[type="submit"]');
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting...';
            try {
                const response = await fetch(`/api/products/${productId}/reviews`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify(data),
                });
                const result = await response.json();
                if (result.success) {
                    this.showNotification('Thank you! Your review has been submitted.', 'success');
                    formContainer?.setAttribute('hidden', '');
                    reviewForm.reset();
                    ratingStars?.forEach((s) => s.classList.remove('active'));
                    // Optionally reload to show new review
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                }
                else if (response.status === 401) {
                    this.showNotification('Please sign in to submit a review', 'warning');
                    setTimeout(() => {
                        window.location.href = '/auth/login?redirect=' + encodeURIComponent(window.location.pathname);
                    }, 1500);
                }
                else {
                    this.showNotification(result.error || 'Failed to submit review', 'error');
                }
            }
            catch (error) {
                this.showNotification('Failed to submit review', 'error');
            }
            finally {
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit Review';
            }
        });
        // Vote helpful/not helpful
        reviewsContainer.querySelectorAll('[data-vote]').forEach((btn) => {
            btn.addEventListener('click', async () => {
                const button = btn;
                const reviewId = button.dataset.reviewId;
                const voteType = button.dataset.vote;
                if (!reviewId || !voteType)
                    return;
                try {
                    const response = await fetch(`/api/reviews/${reviewId}/vote`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                        },
                        body: JSON.stringify({ voteType }),
                    });
                    const result = await response.json();
                    if (result.success) {
                        // Update the count in the button text
                        const countMatch = button.textContent?.match(/\((\d+)\)/);
                        if (countMatch) {
                            const currentCount = parseInt(countMatch[1]);
                            button.textContent = button.textContent?.replace(`(${currentCount})`, `(${currentCount + 1})`) || '';
                        }
                        button.disabled = true;
                        this.showNotification('Thanks for your feedback!', 'success');
                    }
                }
                catch (error) {
                    // Silently fail for votes
                }
            });
        });
        // Update star display based on rating attribute
        reviewsContainer.querySelectorAll('.stars[data-rating]').forEach((starsEl) => {
            const rating = parseFloat(starsEl.dataset.rating || '0');
            const stars = starsEl.querySelectorAll('svg');
            stars.forEach((star, i) => {
                if (i < Math.floor(rating)) {
                    star.classList.add('filled');
                }
                else if (i < rating) {
                    star.classList.add('half-filled');
                }
            });
        });
    }
    showNotification(message, type = 'success') {
        // Check if toast container exists, create if not
        let container = document.querySelector('.toast-container');
        if (!container) {
            container = document.createElement('div');
            container.className = 'toast-container';
            document.body.appendChild(container);
        }
        // Create toast
        const toast = document.createElement('div');
        toast.className = `toast toast--${type}`;
        toast.innerHTML = `
      <div class="toast__content">
        <span class="toast__message">${message}</span>
        <button class="toast__close" aria-label="Close">&times;</button>
      </div>
    `;
        container.appendChild(toast);
        // Auto dismiss
        setTimeout(() => {
            toast.classList.add('toast--hiding');
            setTimeout(() => toast.remove(), 300);
        }, 3000);
        // Manual dismiss
        toast.querySelector('.toast__close')?.addEventListener('click', () => {
            toast.classList.add('toast--hiding');
            setTimeout(() => toast.remove(), 300);
        });
    }
}
// Initialize app
const app = new App();
// Expose global API
window.MunchMakers = {
    cart: app['cart'],
    refreshCart: async () => app['cart'].refresh(),
    addToCart: async (productId, quantity = 1, options) => {
        return app['cart'].addItem(productId, quantity, undefined, options);
    },
};
export default app;
